import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { Icons } from '../../../util/Icons';

/** Semantic tone of the panel — drives icon + colour. */
export type AraPanelType = 'info' | 'warning' | 'error';

/** Icon per panel type (from the Icons catalog). */
const PANEL_ICONS: Record<AraPanelType, string> = {
	info: 'infoOutline',
	warning: 'warningOutline',
	error: 'cancelCircleOutline',
};

/** Default bold header per panel type. Overridable via the `header` input. */
const PANEL_HEADERS: Record<AraPanelType, string> = {
	info: 'Info',
	warning: 'Warnung',
	error: 'Fehler',
};

/**
 * Inline feedback panel: a fixed leading icon + colour per `type`, and a message.
 * The consumer only provides `type` and the text — via the `message` input or projected
 * content; everything else (icon, colours, layout) is baked in.
 *
 * ```html
 * <ara-panel type="info" message="…"></ara-panel>
 * <ara-panel type="warning">Rich <b>content</b> …</ara-panel>
 * ```
 */
@Component({
	selector: 'ara-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './ara-panel.component.html',
	styleUrl: './ara-panel.component.scss',
	imports: [MatIcon],
	host: { class: 'ara-panel', '[attr.data-type]': 'type()', role: 'note' },
})
export class AraPanelComponent {
	protected readonly icons = inject(Icons);

	/** Semantic tone — picks the leading icon, header and colour scheme. */
	readonly type = input.required<AraPanelType>();
	/** Bold header text. Defaults to the type label (Info / Warnung / Fehler); pass '' to hide it. */
	readonly header = input<string>();
	/** Message text. Optional — omit it to project rich content instead. */
	readonly message = input<string>('');

	protected readonly iconPath = computed<string>(() => this.icons.get(PANEL_ICONS[this.type()]));
	protected readonly headerText = computed<string>(() => this.header() ?? PANEL_HEADERS[this.type()]);
}
